import React, { useEffect, useState } from "react";
import { Sparkles, Tv, Copy, Magnet as MagnetIcon, Send, Loader2, Check, AlertTriangle, Cloud, CloudOff, CloudUpload, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * "Install on my device" button — appears only on the recommended row of
 * a torrent list. The dropdown contents adapt to whether the user is
 * signed-in (shows paired TVs) and what device profile they've picked.
 *
 * Paired-device path:
 *   POST /api/devices/cast → backend queues the magnet for the TV.
 *   The Android app polls /api/devices/cast/pending and launches PlayerActivity.
 *
 * Fallback paths (no paired TVs / guest):
 *   - Open magnet locally (default client)
 *   - Copy magnet
 */
export default function OneTapInstall({ row, recommendedFor, tmdbId, mediaType }) {
  const { user } = useAuth();
  const [devices, setDevices] = useState(null);   // null = loading, [] = none
  const [castingTo, setCastingTo] = useState(null);
  const [recentSuccess, setRecentSuccess] = useState(null);
  const [confirmDevice, setConfirmDevice] = useState(null);
  // Cloud status — drives whether casting will actually play on the TV.
  // { in_cloud, items, request_status, is_admin } | null while loading
  const [cloud, setCloud] = useState(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!user) { setDevices([]); return; }
    let alive = true;
    api.get("/devices/mine")
      .then((r) => { if (alive) setDevices(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setDevices([]); });
    return () => { alive = false; };
  }, [user]);

  // Look up whether this TMDB title is already cached so we can show a
  // "Cloud-ready" or "Not in cloud" indicator BEFORE the user clicks.
  useEffect(() => {
    if (!user || !tmdbId || !mediaType) { setCloud(null); return; }
    let alive = true;
    const params = { tmdb_id: tmdbId, media_type: mediaType };
    if (row?.season != null)  params.season  = row.season;
    if (row?.episode != null) params.episode = row.episode;
    api.get("/library/exists", { params })
      .then((r) => { if (alive) setCloud(r.data || null); })
      .catch(() => { if (alive) setCloud({ in_cloud: false }); });
    return () => { alive = false; };
  }, [user, tmdbId, mediaType, row?.season, row?.episode]);

  const requestAdd = async () => {
    if (!tmdbId || !mediaType) return;
    setRequesting(true);
    try {
      await api.post("/library/requests", {
        tmdb_id: tmdbId,
        media_type: mediaType,
        season: row?.season ?? null,
        note: `Auto-requested via Install button — ${row?.name?.slice(0, 100) || ""}`,
      });
      setCloud((c) => ({ ...(c || {}), request_status: "open" }));
      toast.success("Logged a request to add this title to the cloud.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't log request");
    } finally {
      setRequesting(false);
    }
  };

  const doCast = async (device) => {
    if (!row?.magnet && !tmdbId) {
      toast.error("Nothing playable for this row");
      return;
    }
    setCastingTo(device.device_id);
    try {
      await api.post("/devices/cast", {
        device_id: device.device_id,
        magnet: row.magnet || null,
        info_hash: row.info_hash || null,
        title: row.name || null,
        recommended_for: recommendedFor || null,
        // Cloud-library hints — let the backend swap the magnet for a real
        // OpenDrive stream URL when the title is already cached. Without
        // this the Fire TV would need a torrent client to do anything.
        tmdb_id: tmdbId || null,
        media_type: mediaType || null,
        season: row.season ?? null,
        episode: row.episode ?? null,
        preferred_quality: row.resolution || null,
      });
      setRecentSuccess(device.device_id);
      toast.success(`Sent to ${device.device_name || "your TV"} — it'll start in a few seconds.`);
      setTimeout(() => setRecentSuccess(null), 4000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "not_in_cloud_library") {
        toast.error(
          `${row.name?.slice(0, 60) || "This title"} isn't cached in your cloud library yet — your TV can't play raw torrents. Ask an admin to add it, or pick a title from the Downloads page.`,
          { duration: 7000 },
        );
      } else if (detail === "tv_cast_needs_season_episode") {
        toast.error("Pick a specific episode from the show's detail page before sending to TV.");
      } else if (detail === "device_not_found") {
        toast.error("That TV is no longer paired with your account.");
      } else {
        toast.error(detail || "Could not send to device");
      }
    } finally {
      setCastingTo(null);
    }
  };

  // Two-step send: if the TV is busy, surface a confirmation step so the
  // sender doesn't yank a movie from someone mid-watch.
  const castTo = (device) => {
    if (device.is_busy && device.now_playing?.title) {
      setConfirmDevice(device);
    } else {
      doCast(device);
    }
  };

  const confirmCast = async () => {
    if (!confirmDevice) return;
    const d = confirmDevice;
    setConfirmDevice(null);
    await doCast(d);
  };

  const copyMagnet = async () => {
    if (!row?.magnet) return toast.error("Magnet unavailable");
    try {
      await navigator.clipboard.writeText(row.magnet);
      toast.success("Magnet copied");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const openMagnet = () => {
    if (!row?.magnet) return toast.error("Magnet unavailable");
    window.location.href = row.magnet;
  };

  // Visual state for the trigger pill: cloud-ready / not-in-cloud / loading.
  const cloudKnown = cloud !== null;
  const inCloud   = cloud?.in_cloud === true;
  const hasOpenRequest = cloud?.request_status === "open";

  const triggerClasses = !cloudKnown
    ? "bg-emerald-400 hover:bg-emerald-300 text-black shadow-[0_0_12px_-2px_rgba(52,211,153,0.55)]"
    : inCloud
      ? "bg-emerald-400 hover:bg-emerald-300 text-black shadow-[0_0_12px_-2px_rgba(52,211,153,0.55)]"
      : "bg-amber-400 hover:bg-amber-300 text-black shadow-[0_0_12px_-2px_rgba(251,191,36,0.55)]";

  const triggerLabel = !cloudKnown ? "Install"
    : inCloud   ? "Cast to TV"
    : "Not in cloud";

  const triggerTitle = !cloudKnown
    ? "Install / play this torrent on your device"
    : inCloud
      ? "This title is cached in your cloud — TVs can play it instantly."
      : "This title isn't in your cloud library yet — TVs can't play raw torrents.";

  const TriggerIcon = !cloudKnown ? Sparkles : inCloud ? CheckCircle2 : CloudOff;

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="one-tap-install-trigger"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-display font-extrabold uppercase tracking-wider transition-colors ${triggerClasses}`}
          title={triggerTitle}
        >
          <TriggerIcon className="w-3 h-3" />
          {triggerLabel}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-[var(--tt-surface)] border-[var(--tt-border)] text-white min-w-[280px]"
        data-testid="one-tap-install-menu"
      >
        {/* Cloud status banner — first thing the user sees. */}
        {cloudKnown && (
          inCloud ? (
            <div
              className="px-2.5 py-2 mb-1 rounded-md bg-emerald-400/10 border border-emerald-400/30 flex items-start gap-2"
              data-testid="one-tap-cloud-ready"
            >
              <Cloud className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-none" />
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-emerald-300">Cloud-ready</div>
                <div className="text-[10px] text-[var(--tt-text-muted)] leading-snug">
                  Cached at {cloud.items?.[0]?.quality || "library quality"} — TVs play instantly.
                </div>
              </div>
            </div>
          ) : (
            <div
              className="px-2.5 py-2 mb-1 rounded-md bg-amber-400/10 border border-amber-400/30"
              data-testid="one-tap-cloud-missing"
            >
              <div className="flex items-start gap-2">
                <CloudOff className="w-3.5 h-3.5 mt-0.5 text-amber-400 flex-none" />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold text-amber-300">Not in your cloud yet</div>
                  <div className="text-[10px] text-[var(--tt-text-muted)] leading-snug">
                    TVs can&apos;t play raw torrents. Add this title to the cloud so it can be cast.
                  </div>
                </div>
              </div>
              {cloud.is_admin ? (
                <a
                  href={`/admin?tab=library&prefill_tmdb=${tmdbId}&prefill_type=${mediaType}`}
                  data-testid="one-tap-admin-add"
                  className="mt-2 inline-flex items-center justify-center gap-1.5 w-full px-2.5 py-1.5 rounded-md bg-amber-400 hover:bg-amber-300 text-black text-[10px] font-display font-extrabold uppercase tracking-wider transition-colors"
                >
                  <CloudUpload className="w-3 h-3" />
                  Add to cloud now
                </a>
              ) : hasOpenRequest ? (
                <div
                  className="mt-2 text-[10px] text-amber-200 text-center px-2 py-1 rounded-md bg-amber-400/10 border border-amber-400/20"
                  data-testid="one-tap-request-pending"
                >
                  ⏳ Request logged — an admin will add it soon
                </div>
              ) : (
                <button
                  type="button"
                  onClick={requestAdd}
                  disabled={requesting}
                  data-testid="one-tap-request-add"
                  className="mt-2 inline-flex items-center justify-center gap-1.5 w-full px-2.5 py-1.5 rounded-md bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black text-[10px] font-display font-extrabold uppercase tracking-wider transition-colors"
                >
                  {requesting
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <CloudUpload className="w-3 h-3" />}
                  Request admin to add
                </button>
              )}
            </div>
          )
        )}

        {/* Paired devices section */}
        {devices === null ? (
          <DropdownMenuItem disabled className="text-[var(--tt-text-muted)]">
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading your devices…
          </DropdownMenuItem>
        ) : devices.length > 0 ? (
          <>
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
              Send to TV
            </DropdownMenuLabel>
            {devices.map((d) => {
              const busy = castingTo === d.device_id;
              const ok = recentSuccess === d.device_id;
              const tvBusy = d.is_busy && d.now_playing?.title;
              // Hard-disable the device row when the title isn't in cloud
              // — clicking would just bounce off the backend's 422.
              const cannotPlay = cloudKnown && !inCloud;
              return (
                <DropdownMenuItem
                  key={d.device_id}
                  onSelect={(e) => {
                    e.preventDefault();
                    if (cannotPlay) {
                      toast.error("Add this title to the cloud first — TVs can't play raw torrents.");
                      return;
                    }
                    castTo(d);
                  }}
                  disabled={busy || cannotPlay}
                  data-testid={`one-tap-cast-${d.device_id}`}
                  className={`cursor-pointer focus:bg-emerald-400/10 ${cannotPlay ? "opacity-50" : ""}`}
                  title={cannotPlay ? "Add this title to the cloud first" : ""}
                >
                  {ok
                    ? <Check className="w-3.5 h-3.5 mr-2 text-emerald-400" />
                    : busy
                      ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin text-emerald-400" />
                      : tvBusy
                        ? <span className="relative inline-flex w-3.5 h-3.5 mr-2 items-center justify-center">
                            <span className="absolute inline-flex w-2 h-2 rounded-full bg-amber-400 opacity-75 animate-ping" />
                            <span className="relative inline-flex w-2 h-2 rounded-full bg-amber-400" />
                          </span>
                        : <Tv className={`w-3.5 h-3.5 mr-2 ${cannotPlay ? "text-[var(--tt-text-muted)]" : "text-emerald-400"}`} />}
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold truncate">{d.device_name || "Fire TV"}</span>
                    <span className={`text-[10px] truncate ${tvBusy ? "text-amber-400" : "text-[var(--tt-text-muted)]"}`}>
                      {ok ? "Sent — starting now…"
                          : busy ? "Sending…"
                          : tvBusy ? `Playing: ${d.now_playing.title}`
                          : `${d.platform || "android_tv"}${d.device_model ? ` · ${d.device_model}` : ""}`}
                    </span>
                  </div>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator className="bg-[var(--tt-border)]" />
          </>
        ) : user ? (
          <>
            <DropdownMenuItem disabled className="text-[var(--tt-text-muted)] text-[11px]">
              No paired TVs yet — <a href="/link" className="underline ml-1">pair one →</a>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[var(--tt-border)]" />
          </>
        ) : (
          <>
            <DropdownMenuItem disabled className="text-[var(--tt-text-muted)] text-[11px]">
              Sign in to send to your TV
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[var(--tt-border)]" />
          </>
        )}

        {/* Always-available fallbacks */}
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); openMagnet(); }}
          data-testid="one-tap-open-magnet"
          className="cursor-pointer"
        >
          <Send className="w-3.5 h-3.5 mr-2 text-[var(--tt-text-muted)]" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Open in torrent client</span>
            <span className="text-[10px] text-[var(--tt-text-muted)]">
              Launches your system default (qBittorrent, Transmission, …)
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); copyMagnet(); }}
          data-testid="one-tap-copy-magnet"
          className="cursor-pointer"
        >
          <Copy className="w-3.5 h-3.5 mr-2 text-[var(--tt-text-muted)]" />
          <span className="text-sm font-semibold">Copy magnet link</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[var(--tt-border)]" />
        <DropdownMenuItem disabled className="text-[10px] text-[var(--tt-text-faint)] cursor-default">
          <MagnetIcon className="w-3 h-3 mr-2" />
          {row.info_hash ? `${row.info_hash.slice(0, 12)}…` : "magnet:"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    {/* "Interrupt current playback?" confirmation. Rendered as a centered
        modal because Sonner toasts can't host two buttons cleanly. */}
    {confirmDevice && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
        onClick={() => setConfirmDevice(null)}
        data-testid="one-tap-confirm-overlay"
      >
        <div
          className="max-w-md w-full bg-[var(--tt-surface)] border border-amber-400/40 rounded-2xl p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-none w-10 h-10 rounded-full bg-amber-400/15 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-bold leading-tight">
                {confirmDevice.device_name || "Your TV"} is busy
              </h3>
              <p className="text-xs text-[var(--tt-text-muted)] mt-1">
                It&apos;s currently playing
                {" "}
                <span className="text-amber-300 font-semibold">
                  {confirmDevice.now_playing?.title || "something"}
                </span>
                . Send <span className="text-white font-semibold truncate inline-block max-w-[18ch] align-bottom">{row?.name || "this torrent"}</span> anyway?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmDevice(null)}
              data-testid="one-tap-confirm-cancel"
              className="px-4 py-2 rounded-md text-sm font-semibold text-[var(--tt-text-muted)] hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmCast}
              data-testid="one-tap-confirm-interrupt"
              className="px-4 py-2 rounded-md text-sm font-bold bg-amber-400 hover:bg-amber-300 text-black transition-colors"
            >
              Interrupt &amp; play
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
